// ========================================
// Asistencia (diseño A, oct. 2026): calendario del mes y «Sin pasar · últimos 30 días»
// a la izquierda, las clases del día en el centro y la lista de la clase elegida
// dentro de la página, a la derecha.
// ========================================

const AttendanceView = {
    canManage() {
        const user=AuthManager.getCurrentUser();
        return ['admin','coordinator'].includes(user?.role) || AuthManager.hasModule('asistencia','manage') ||
            user?.role==='professor' && !!user.professorId && this.activeSession?.professorId===user.professorId;
    },
    // Sessions of one day that this user takes attendance for. The calendar and the
    // day panel share it: a professor only sees days with their own classes.
    sessionsOn(dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dayOfWeek = new Date(y, m - 1, d).getDay();
        const sessions = (typeof ClassService !== 'undefined' ? ClassService.getClasses() : []).filter(s => {
            if (s.dayOfWeek !== dayOfWeek) return false;
            if (s.startDate && dateStr < s.startDate) return false;
            if (s.endDate && dateStr > s.endDate) return false;
            return true;
        });
        const currentUser = typeof AuthManager !== 'undefined' ? AuthManager.getCurrentUser() : null;
        const isProf = typeof AuthManager !== 'undefined' && AuthManager.isProfessor() && !AuthManager.hasModule('asistencia');
        return isProf && currentUser ? sessions.filter(s => s.professorId === currentUser.professorId) : sessions;
    },

    selectedDate: null,
    currentYear: null,
    currentMonth: null,
    activeSession: null,
    attendanceData: { present: [], absent: [], justified: [] },
    enrolledStudents: [],
    _dirty: false,

    _pad(n) { return String(n).padStart(2, '0'); },
    _iso(d) { return `${d.getFullYear()}-${this._pad(d.getMonth() + 1)}-${this._pad(d.getDate())}`; },
    _todayIso() { return this._iso(new Date()); },
    _nowHm() { const d = new Date(); return `${this._pad(d.getHours())}:${this._pad(d.getMinutes())}`; },
    _esc(s) { return Utils.sanitizeHTML(String(s == null ? '' : s)); },
    _record(sessionId, dateStr) {
        return (this._records || []).find(r => String(r.classSessionId) === String(sessionId) && r.date === dateStr) || null;
    },
    // Una clase «tocaba» lista si ya empezó (días pasados, o hoy a partir de su hora).
    _isDue(session, dateStr) {
        const today = this._todayIso();
        return dateStr < today || (dateStr === today && (session.startTime || '00:00') <= this._nowHm());
    },
    _names(session) {
        const formation = typeof DataManager !== 'undefined' ? DataManager.getById(DataManager.KEYS.FORMATIONS, session.formationId) : null;
        const subject = typeof DataManager !== 'undefined' ? DataManager.getById(DataManager.KEYS.SUBJECTS, session.subjectId) : null;
        const professor = typeof DataManager !== 'undefined' ? DataManager.getById(DataManager.KEYS.PROFESSORS, session.professorId) : null;
        return {
            formation: formation ? (formation.commercialName || formation.name) : 'Formación',
            subject: subject ? subject.name : 'Asignatura',
            professor: professor ? `${professor.firstName || ''} ${professor.lastName || ''}`.trim() : (subject?.professorName || '')
        };
    },
    _dateLong(dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const text = new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        return text.charAt(0).toUpperCase() + text.slice(1);
    },
    _dateShort(dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const text = new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
        return text.charAt(0).toUpperCase() + text.slice(1);
    },

    _initCurrentDate() {
        const today = new Date();
        this.selectedDate = this._iso(today);
        this.currentYear = today.getFullYear();
        this.currentMonth = today.getMonth();
    },

    render() {
        this._initCurrentDate();
        return `
            <div class="view-container dnr-proposal att-view" style="padding-top: 1rem;">
                <style ${SafeHTML.styleAttr}>
                    .att-view { gap: 0.85rem; }
                    .att-view [hidden] { display: none !important; }
                    .att-top { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; }
                    .att-title { flex: 1; margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 2.4rem; letter-spacing: 0.02em; line-height: 1; color: var(--dnr-ink); }
                    .att-pill { font-size: 0.8rem; font-weight: 700; padding: 5px 10px; border-radius: 999px; background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); }
                    .att-pill.is-bad { background: #FBE4E1; color: #8E2A21; }
                    .att-pill.is-ok { background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                    .att-label { font: 700 0.68rem var(--dnr-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--dnr-ink-mute); }
                    .att-card { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 16px; box-shadow: var(--dnr-shadow-card); overflow: hidden; }
                    .att-btn { min-height: 34px; padding: 0 12px; border: 1px solid var(--dnr-line); border-radius: 10px; background: var(--dnr-surface); color: var(--dnr-ink); font-weight: 700; font-size: 0.82rem; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
                    .att-btn:hover { border-color: var(--dnr-teal-400); }
                    .att-btn.is-all { background: var(--dnr-teal-100); border-color: #BFE0D8; color: var(--dnr-teal-700); }
                    .att-btn.is-primary { min-height: 40px; padding: 0 18px; border: 0; background: var(--dnr-teal-600); color: #fff; font-size: 0.9rem; }
                    .att-btn:disabled { opacity: 0.6; cursor: default; }
                    #attendance-flowlu-layout { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 0.85rem; align-items: start; }
                    .att-side { display: flex; flex-direction: column; gap: 0.85rem; }
                    .att-calendar-col { padding: 0.75rem; }
                    .att-month { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
                    .att-month b { flex: 1; color: var(--dnr-ink); }
                    .att-month .att-btn { min-height: 30px; padding: 0 9px; }
                    .att-cal { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 3px; text-align: center; }
                    .att-dow { font: 700 0.62rem var(--dnr-mono); color: var(--dnr-ink-mute); padding: 2px 0; }
                    .att-day { min-height: 34px; border: 0; border-radius: 8px; font-weight: 700; font-size: 0.8rem; cursor: pointer; background: transparent; color: var(--dnr-ink-faint); font-family: inherit; }
                    .att-day.has-classes { background: var(--dnr-surface-sunken); color: var(--dnr-ink); }
                    .att-day.is-ok { background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                    .att-day.is-bad { background: #FBE4E1; color: #8E2A21; }
                    .att-day.is-today { box-shadow: inset 0 0 0 1.5px var(--dnr-ink); }
                    .att-day.is-sel { background: var(--dnr-ink); color: #fff; }
                    .att-legend { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; font-size: 0.72rem; font-weight: 700; }
                    .att-legend span:nth-child(1) { color: var(--dnr-teal-700); }
                    .att-legend span:nth-child(2) { color: #8E2A21; }
                    .att-legend span:nth-child(3) { color: var(--dnr-ink-mute); }
                    .att-head { padding: 0.65rem 0.85rem; border-bottom: 1px solid var(--dnr-line); }
                    .att-head h2 { margin: 2px 0 0; font-family: var(--dnr-display); font-weight: 400; font-size: 1.7rem; letter-spacing: 0.03em; line-height: 1; color: var(--dnr-ink); }
                    .att-list { padding: 6px; display: flex; flex-direction: column; gap: 3px; max-height: 340px; overflow-y: auto; }
                    .att-pend { display: flex; gap: 8px; align-items: baseline; padding: 7px 8px; border: 0; border-radius: 9px; background: transparent; text-align: left; cursor: pointer; font-size: 0.88rem; color: var(--dnr-ink); font-family: inherit; }
                    .att-pend:hover { background: var(--dnr-surface-sunken); }
                    .att-pend-date { font: 700 0.72rem var(--dnr-mono); color: #8E2A21; min-width: 52px; }
                    .att-pend small { display: block; font-size: 0.75rem; color: var(--dnr-ink-mute); }
                    .att-empty { margin: 0; padding: 0.8rem; font-size: 0.85rem; color: var(--dnr-ink-mute); }
                    .att-main { display: grid; grid-template-columns: 340px minmax(0, 1fr); gap: 0.85rem; align-items: start; }
                    .att-sessions-col .att-list { max-height: 640px; }
                    .att-session { display: flex; flex-direction: column; gap: 2px; text-align: left; padding: 10px 12px; border-radius: 12px; cursor: pointer; color: var(--dnr-ink); font-family: inherit; background: var(--dnr-surface); border: 1px solid var(--dnr-line); }
                    .att-session.is-sel { background: var(--dnr-teal-100); border-color: var(--dnr-teal-600); }
                    .att-session-top { display: flex; align-items: center; gap: 8px; }
                    .att-session-top span:first-child { font: 700 0.75rem var(--dnr-mono); color: var(--dnr-ink-mute); flex: 1; }
                    .att-session b { font-size: 0.95rem; }
                    .att-session-meta { font-size: 0.8rem; color: var(--dnr-ink-mute); display: flex; flex-wrap: wrap; gap: 0 0.4rem; }
                    .att-state { font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); white-space: nowrap; }
                    .att-state.is-ok { background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                    .att-state.is-bad { background: #FBE4E1; color: #8E2A21; }
                    .att-modal-box { min-height: 200px; }
                    .att-sheet-head { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 0.85rem 1rem; border-bottom: 1px solid var(--dnr-line); }
                    .att-sheet-head > div { flex: 1; min-width: 220px; }
                    .att-sheet-head h2 { margin: 0; font-family: var(--dnr-display); font-weight: 400; font-size: 1.9rem; letter-spacing: 0.03em; line-height: 1; color: var(--dnr-ink); }
                    .att-sheet-head p { margin: 2px 0 0; font-size: 0.85rem; color: var(--dnr-ink-mute); }
                    .att-counts { display: flex; flex-wrap: wrap; gap: 6px; padding: 0.6rem 1rem; border-bottom: 1px solid var(--dnr-line); font-size: 0.8rem; font-weight: 700; }
                    .att-counts span { padding: 3px 9px; border-radius: 999px; }
                    .att-counts .c-p { background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                    .att-counts .c-a { background: #FBE4E1; color: #8E2A21; }
                    .att-counts .c-j { background: var(--dnr-amber-100); color: #6B3A0B; }
                    .att-counts .c-n { background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); }
                    #modal-students-list { max-height: 560px; overflow-y: auto; }
                    .att-student { display: flex; align-items: center; gap: 12px; padding: 8px 1rem; border-bottom: 1px solid var(--dnr-line); }
                    .att-avatar { width: 34px; height: 34px; flex-shrink: 0; border-radius: 50%; background: var(--dnr-surface-sunken); display: inline-flex; align-items: center; justify-content: center; font-weight: 700; color: var(--dnr-ink-mute); overflow: hidden; }
                    .att-avatar img { width: 100%; height: 100%; object-fit: cover; }
                    .att-student-name { flex: 1; min-width: 0; }
                    .att-student-name a { font-weight: 700; color: var(--dnr-ink); text-decoration: none; cursor: pointer; }
                    .att-student-name a:hover { color: var(--dnr-teal-700); text-decoration: underline; }
                    .att-student-name small { display: block; font-size: 0.75rem; color: var(--dnr-ink-mute); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                    .att-seg { display: inline-flex; gap: 3px; padding: 3px; background: var(--dnr-surface-sunken); border-radius: 10px; flex-shrink: 0; }
                    .att-seg button { min-height: 32px; padding: 0 12px; border: 0; border-radius: 8px; font-weight: 700; font-size: 0.8rem; cursor: pointer; background: transparent; color: var(--dnr-ink-mute); font-family: inherit; }
                    .att-seg button:disabled { cursor: default; }
                    .att-seg button.is-on[data-status="present"] { background: var(--dnr-teal-600); color: #fff; }
                    .att-seg button.is-on[data-status="absent"] { background: #B4483C; color: #fff; }
                    .att-seg button.is-on[data-status="justified"] { background: #C27A1A; color: #fff; }
                    .att-sheet-foot { display: flex; align-items: center; gap: 10px; padding: 0.75rem 1rem; background: #FAFCFB; }
                    .att-sheet-foot span { flex: 1; font-size: 0.82rem; color: var(--dnr-ink-mute); }
                    .att-sheet-foot span.is-dirty { color: #7A4210; font-weight: 700; }
                    @media (max-width: 1280px) {
                        .att-main { grid-template-columns: minmax(0, 1fr); }
                        .att-sessions-col .att-list { max-height: none; }
                    }
                    /* Móvil: calendario arriba, luego las clases y la lista; todo a una columna. */
                    @media (max-width: 992px) {
                        #attendance-flowlu-layout { grid-template-columns: minmax(0, 1fr) !important; gap: 0.85rem !important; }
                        .att-session-meta { flex-wrap: wrap; }
                        .att-student { flex-wrap: wrap; }
                        .att-seg { width: 100%; }
                        .att-seg button { flex: 1; }
                        #modal-students-list { max-height: none; }
                    }
                </style>

                <header class="att-top">
                    <h1 class="att-title">Asistencia</h1>
                    <span id="att-top-pending" class="att-pill"></span>
                    <span id="att-top-average" class="att-pill" title="Alumnos que asistieron en las listas pasadas este mes"></span>
                </header>

                <div id="attendance-flowlu-layout">
                    <aside class="att-side">
                        <section class="att-card att-calendar-col">
                            <div class="att-month">
                                <b id="att-calendar-month-label"></b>
                                <button type="button" class="att-btn" aria-label="Mes anterior" ${UIEvents.attr("click", function(event,captured){AttendanceView.changeMonth(-1);}, [])}><i class="fas fa-chevron-left"></i></button>
                                <button type="button" class="att-btn" aria-label="Mes siguiente" ${UIEvents.attr("click", function(event,captured){AttendanceView.changeMonth(1);}, [])}><i class="fas fa-chevron-right"></i></button>
                            </div>
                            <div id="att-mini-calendar-grid"></div>
                            <div class="att-legend"><span>■ Todas pasadas</span><span>■ Falta alguna</span><span>■ Por llegar</span></div>
                        </section>
                        <section class="att-card">
                            <div class="att-head"><span class="att-label">Sin pasar · últimos 30 días</span></div>
                            <div class="att-list" id="att-pending-list"></div>
                        </section>
                    </aside>

                    <div class="att-main">
                        <section class="att-card att-sessions-col">
                            <div class="att-head">
                                <span class="att-label" id="att-selected-date-title"></span>
                                <h2 id="att-selected-date-subtitle"></h2>
                            </div>
                            <div class="att-list" id="attendance-sessions-container"></div>
                        </section>
                        <section class="att-card att-modal-box" id="att-sheet"></section>
                    </div>
                </div>
            </div>
        `;
    },

    init() {
        this._initCurrentDate();
        this._records = [];
        this.activeSession = null;
        this._dirty = false;
        this.renderMiniCalendar();
        this.loadSessions();
        this.refreshStatuses();
    },

    async refreshStatuses() {
        const generation = this._statusGeneration = (this._statusGeneration || 0) + 1;
        const token = AuthManager.getCsrfToken();
        const today = new Date();
        const monthStart = `${this.currentYear}-${this._pad(this.currentMonth + 1)}-01`;
        const back30 = this._iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30));
        const since = [monthStart, back30, this._iso(today)].sort()[0];
        try {
            const result = await AttendanceService.request('/api/attendance/summary?since=' + since);
            if (generation !== this._statusGeneration || token !== AuthManager.getCsrfToken()) return;
            this._records = result.records || [];
        } catch {
            if (generation !== this._statusGeneration || token !== AuthManager.getCsrfToken()) return;
            this._records = [];
            if (typeof Notifications !== 'undefined') Notifications.show('No se pudo actualizar el estado de las listas de asistencia.', 'error');
        }
        this.renderMiniCalendar();
        this.loadSessions();
    },

    changeMonth(delta) {
        this.currentMonth += delta;
        if (this.currentMonth < 0) {
            this.currentMonth = 11;
            this.currentYear--;
        } else if (this.currentMonth > 11) {
            this.currentMonth = 0;
            this.currentYear++;
        }
        this.renderMiniCalendar();
        this.refreshStatuses();
    },

    selectDate(dateStr) {
        if (!this._confirmLeave(() => this.selectDate(dateStr))) return;
        this.selectedDate = dateStr;
        this.activeSession = null;
        const [y, m] = dateStr.split('-').map(Number);
        if (y !== this.currentYear || m - 1 !== this.currentMonth) {
            this.currentYear = y;
            this.currentMonth = m - 1;
            this.refreshStatuses();
        }
        this.renderMiniCalendar();
        this.loadSessions();
    },

    // Avisa antes de cambiar de clase o de día con marcas sin guardar.
    _confirmLeave(retry) {
        if (!this._dirty) return true;
        SafeHTML.fire({
            title: 'Hay cambios sin guardar',
            text: 'Si sigues, se pierden las marcas de esta lista.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Seguir sin guardar',
            cancelButtonText: 'Volver a la lista',
            reverseButtons: true
        }).then(result => {
            if (!result.isConfirmed) return;
            this._dirty = false;
            retry();
        });
        return false;
    },

    renderMiniCalendar() {
        const gridEl = document.getElementById('att-mini-calendar-grid');
        const labelEl = document.getElementById('att-calendar-month-label');
        if (!gridEl) return;

        const year = this.currentYear, month = this.currentMonth;
        const dateObj = new Date(year, month, 1);
        // A month of another academic year is loaded on demand, then drawn again.
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            FileStorage.needClasses(dateObj, new Date(year, month + 1, 0), () => { this.renderMiniCalendar(); this.loadSessions(); });
        }
        if (labelEl) { const label = dateObj.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); labelEl.textContent = label.charAt(0).toUpperCase() + label.slice(1); }

        const firstDayIndex = (dateObj.getDay() + 6) % 7; // Monday = 0
        const totalDays = new Date(year, month + 1, 0).getDate();
        const todayStr = this._todayIso();

        let html = '<div class="att-cal">' + ['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => `<span class="att-dow">${d}</span>`).join('');
        for (let p = 0; p < firstDayIndex; p++) html += '<span></span>';
        for (let day = 1; day <= totalDays; day++) {
            const dateStr = `${year}-${this._pad(month + 1)}-${this._pad(day)}`;
            const daySessions = this.sessionsOn(dateStr);
            const cls = ['att-day'];
            if (daySessions.length) {
                cls.push('has-classes');
                const due = daySessions.filter(s => this._isDue(s, dateStr));
                if (due.some(s => !this._record(s.id, dateStr))) cls.push('is-bad');
                else if (due.length) cls.push('is-ok');
            }
            if (dateStr === todayStr) cls.push('is-today');
            if (dateStr === this.selectedDate) cls.push('is-sel');
            html += `<button type="button" class="${cls.join(' ')}" aria-pressed="${dateStr === this.selectedDate}" aria-label="${this._esc(this._dateLong(dateStr))}"
                ${UIEvents.attr("click", function(event,captured){AttendanceView.selectDate(String(captured[0]));}, [dateStr])}>${day}</button>`;
        }
        html += '</div>';
        SafeHTML.set(gridEl, html);
    },

    // Clases que tocaban lista en los últimos 30 días y no la tienen (la más reciente arriba).
    _pendingList() {
        const today = new Date();
        const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            FileStorage.needClasses(from, today, () => this._renderPending());
        }
        const list = [];
        for (let d = new Date(today); d >= from; d.setDate(d.getDate() - 1)) {
            const dateStr = this._iso(d);
            this.sessionsOn(dateStr)
                .filter(s => this._isDue(s, dateStr) && !this._record(s.id, dateStr))
                .sort((a, b) => String(b.startTime || '').localeCompare(String(a.startTime || '')))
                .forEach(s => list.push({ session: s, date: dateStr }));
        }
        return list;
    },

    _renderPending() {
        const el = document.getElementById('att-pending-list');
        if (!el) return;
        const pending = this._pendingList();
        const top = document.getElementById('att-top-pending');
        if (top) {
            top.textContent = pending.length ? `${pending.length} ${pending.length === 1 ? 'lista sin pasar' : 'listas sin pasar'} · 30 días` : 'Todas las listas al día';
            top.className = `att-pill ${pending.length ? 'is-bad' : 'is-ok'}`;
        }
        SafeHTML.set(el, pending.length ? pending.map(p => {
            const n = this._names(p.session);
            return `<button type="button" class="att-pend" ${UIEvents.attr("click", function(event,captured){AttendanceView.openPending(String(captured[0]),String(captured[1]));}, [p.date, p.session.id])}>
                <span class="att-pend-date">${this._esc(this._dateShort(p.date))}</span>
                <span><b>${this._esc(n.subject)}</b><small>${this._esc(n.formation)}${n.professor ? ` · ${this._esc(n.professor)}` : ''}</small></span>
            </button>`;
        }).join('') : '<p class="att-empty">No queda ninguna lista por pasar.</p>');
    },

    openPending(dateStr, sessionId) {
        if (!this._confirmLeave(() => this.openPending(dateStr, sessionId))) return;
        this.selectDate(dateStr);
        this.openAttendanceSheet(sessionId);
    },

    _renderAverage() {
        const el = document.getElementById('att-top-average');
        if (!el) return;
        const prefix = `${this.currentYear}-${this._pad(this.currentMonth + 1)}`;
        let present = 0, total = 0;
        (this._records || []).filter(r => String(r.date || '').startsWith(prefix)).forEach(r => {
            const p = (r.present || []).length;
            present += p;
            total += p + (r.absent || []).length + (r.justified || []).length;
        });
        el.hidden = !total;
        el.textContent = total ? `Asistencia media · ${Math.round(present / total * 100)} %` : '';
        el.className = 'att-pill is-ok';
    },

    loadSessions() {
        const container = document.getElementById('attendance-sessions-container');
        const titleEl = document.getElementById('att-selected-date-title');
        const subtitleEl = document.getElementById('att-selected-date-subtitle');
        if (!container) return;
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses && this.selectedDate) {
            FileStorage.needClasses(this.selectedDate, this.selectedDate, () => this.loadSessions());
        }
        this._renderPending();
        this._renderAverage();
        if (titleEl) titleEl.textContent = this._dateLong(this.selectedDate);

        const isProf = typeof AuthManager !== 'undefined' && AuthManager.isProfessor() && !AuthManager.hasModule('asistencia');
        const filteredSessions = this.sessionsOn(this.selectedDate).slice().sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
        const completedSessions = filteredSessions.filter(s => this._record(s.id, this.selectedDate)).length;
        if (subtitleEl) {
            subtitleEl.textContent = filteredSessions.length
                ? `${filteredSessions.length} ${filteredSessions.length === 1 ? 'clase' : 'clases'} · ${completedSessions} ${completedSessions === 1 ? 'pasada' : 'pasadas'}`
                : 'Sin clases';
        }

        if (!filteredSessions.length) {
            SafeHTML.set(container, '<p class="att-empty">Este día no hay clases. Elige otro día en el calendario.</p>');
            this.activeSession = null;
            this._renderSheetEmpty('Elige un día con clases para pasar lista.');
            return;
        }

        SafeHTML.set(container, filteredSessions.map(session => {
            const n = this._names(session);
            const record = this._record(session.id, this.selectedDate);
            let state = '', label;
            if (record) {
                const marked = (record.present || []).length + (record.absent || []).length + (record.justified || []).length;
                state = 'is-ok'; label = `Pasada · ${(record.present || []).length}/${marked}`;
            } else if (this._isDue(session, this.selectedDate)) {
                state = 'is-bad'; label = 'Sin pasar';
            } else {
                label = this.selectedDate === this._todayIso() ? `A las ${session.startTime || ''}` : 'Por llegar';
            }
            const selected = this.activeSession && String(this.activeSession.id) === String(session.id);
            return `<button type="button" class="att-session ${selected ? 'is-sel' : ''}" aria-pressed="${!!selected}"
                ${UIEvents.attr("click", function(event,captured){AttendanceView.openAttendanceSheet(String(captured[0]));}, [session.id])}>
                <span class="att-session-top"><span>${this._esc(session.startTime || '')}–${this._esc(session.endTime || '')}</span><span class="att-state ${state}">${this._esc(label)}</span></span>
                <b>${this._esc(n.subject)}</b>
                <span class="att-session-meta"><span>${this._esc(n.formation)}</span>${session.classroom ? `<span>· ${this._esc(session.classroom)}</span>` : ''}${!isProf && n.professor ? `<span>· ${this._esc(n.professor)}</span>` : ''}</span>
            </button>`;
        }).join(''));

        // Al entrar se abre la primera clase que tocaba y no tiene lista (o la primera del día).
        const stillThere = this.activeSession && filteredSessions.some(s => String(s.id) === String(this.activeSession.id));
        if (!stillThere) {
            const first = filteredSessions.find(s => this._isDue(s, this.selectedDate) && !this._record(s.id, this.selectedDate)) || filteredSessions[0];
            this.openAttendanceSheet(String(first.id));
        }
    },

    _renderSheetEmpty(text) {
        const sheet = document.getElementById('att-sheet');
        if (sheet) SafeHTML.set(sheet, `<p class="att-empty" style="padding: 1.5rem;">${this._esc(text)}</p>`);
    },

    async openAttendanceSheet(sessionId) {
        if (this.activeSession && String(this.activeSession.id) !== String(sessionId) &&
            !this._confirmLeave(() => this.openAttendanceSheet(sessionId))) return;
        this._attendanceReady = false;
        this._dirty = false;
        const sheet = document.getElementById('att-sheet');
        try {
            const allSessions = typeof ClassService !== 'undefined' ? ClassService.getClasses() : [];
            const session = allSessions.find(s => String(s.id) === String(sessionId));
            if (!session || !sheet) return;
            this.activeSession = session;
            this._markSelectedSession();
            const n = this._names(session);
            const dateStr = this.selectedDate;
            const enrolledStudents = (typeof StudentService !== 'undefined' ? StudentService.getStudents() : [])
                .filter(student => Array.isArray(student.formations) && student.formations.includes(session.formationId))
                .sort((a, b) => `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`, 'es'));
            this.enrolledStudents = enrolledStudents;
            const canManage = this.canManage();
            SafeHTML.set(sheet, `
                <div class="att-sheet-head">
                    <div>
                        <h2 id="modal-session-title">${this._esc(n.subject)}</h2>
                        <p id="modal-session-subtitle">${this._esc(session.startTime || '')}–${this._esc(session.endTime || '')} · ${this._esc(n.formation)}${session.classroom ? ` · ${this._esc(session.classroom)}` : ''}${n.professor ? ` · ${this._esc(n.professor)}` : ''}</p>
                    </div>
                    ${canManage && enrolledStudents.length ? `<button type="button" class="att-btn is-all" ${UIEvents.attr("click", function(event,captured){AttendanceView.markAllStatus('present');}, [])}><i class="fas fa-check-double"></i> Todos asisten</button>` : ''}
                </div>
                <div class="att-counts" id="att-counts"></div>
                <div id="modal-students-list"><p class="att-empty">Cargando alumnos…</p></div>
                <div class="att-sheet-foot">
                    <span id="att-save-note">Se guarda para el ${this._esc(this._dateLong(dateStr).toLowerCase())}.</span>
                    <button type="button" class="att-btn is-primary" id="btn-save-attendance" ${canManage ? '' : 'hidden'} ${UIEvents.attr("click", function(event,captured){AttendanceView.saveAttendance();}, [])}>Guardar lista</button>
                </div>`);

            const studentsContainer = document.getElementById('modal-students-list');
            if (!enrolledStudents.length) {
                SafeHTML.set(studentsContainer, '<p class="att-empty">No hay alumnos matriculados en esta formación.</p>');
                SafeHTML.set(document.getElementById('att-counts'), '');
                document.getElementById('btn-save-attendance').hidden = true;
                return;
            }

            const record = await AttendanceService.getAttendance(sessionId, dateStr);
            if (!this.activeSession || String(this.activeSession.id) !== String(sessionId) || this.selectedDate !== dateStr) return;
            this.attendanceData = record ? {
                present: record.present || [],
                absent: record.absent || [],
                justified: record.justified || []
            } : { present: [], absent: [], justified: [] };

            SafeHTML.set(studentsContainer, enrolledStudents.map(student => {
                const name = `${student.firstName || ''} ${student.lastName || ''}`.trim();
                const status = this._statusOf(student.id);
                return `
                    <div class="att-student" data-student-id="${this._esc(student.id)}">
                        <span class="att-avatar" aria-hidden="true">${student.photo ? `<img src="${this._esc(student.photo)}" alt="">` : this._esc((student.firstName || 'A').charAt(0))}</span>
                        <span class="att-student-name">
                            <a ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [Utils.studentProfileUrl(student)])} title="Ver ficha del alumno">${this._esc(name)}</a>
                            <small>${this._esc(student.email || 'Sin correo')}</small>
                        </span>
                        <span class="att-seg" role="radiogroup" aria-label="Asistencia de ${this._esc(name)}">
                            ${[['present', 'Asiste'], ['absent', 'Falta'], ['justified', 'Justificada']].map(([value, label]) => `
                                <button type="button" role="radio" data-status="${value}" class="${status === value ? 'is-on' : ''}" aria-checked="${status === value}" ${canManage ? '' : 'disabled'}
                                    ${UIEvents.attr("click", function(event,captured){AttendanceView.updateStudentStatus(String(captured[0]),String(captured[1]));}, [student.id, value])}>${label}</button>`).join('')}
                        </span>
                    </div>`;
            }).join(''));
            this._renderCounts();
            this._attendanceReady = true;
        } catch (error) {
            console.error('Error al abrir la lista de asistencia:', error);
            const container = document.getElementById('modal-students-list');
            if (container) container.textContent = 'No se pudo cargar la asistencia. Comprueba tus permisos y vuelve a abrir la lista.';
        }
    },

    _markSelectedSession() {
        const buttons = document.querySelectorAll('#attendance-sessions-container .att-session');
        const sessions = this.sessionsOn(this.selectedDate).slice().sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
        buttons.forEach((btn, i) => {
            const on = !!(this.activeSession && sessions[i] && String(sessions[i].id) === String(this.activeSession.id));
            btn.classList.toggle('is-sel', on);
            btn.setAttribute('aria-pressed', String(on));
        });
    },

    _statusOf(studentId) {
        if (this.attendanceData.absent.includes(studentId)) return 'absent';
        if (this.attendanceData.justified.includes(studentId)) return 'justified';
        if (this.attendanceData.present.includes(studentId)) return 'present';
        return '';
    },

    _renderCounts() {
        const el = document.getElementById('att-counts');
        if (!el) return;
        const ids = this.enrolledStudents.map(s => s.id);
        const count = status => ids.filter(id => this._statusOf(id) === status).length;
        SafeHTML.set(el, `<span class="c-p">Asisten ${count('present')}</span><span class="c-a">Faltan ${count('absent')}</span><span class="c-j">Justificadas ${count('justified')}</span><span class="c-n">Sin marcar ${count('')}</span>`);
        const note = document.getElementById('att-save-note');
        if (note) {
            note.classList.toggle('is-dirty', this._dirty);
            note.textContent = this._dirty ? 'Cambios sin guardar.' : `Se guarda para el ${this._dateLong(this.selectedDate).toLowerCase()}.`;
        }
    },

    _paintStudent(studentId) {
        const row = [...document.querySelectorAll('#modal-students-list .att-student')].find(r => r.getAttribute('data-student-id') === String(studentId));
        if (!row) return;
        const status = this._statusOf(studentId);
        row.querySelectorAll('button[data-status]').forEach(btn => {
            const on = btn.getAttribute('data-status') === status;
            btn.classList.toggle('is-on', on);
            btn.setAttribute('aria-checked', String(on));
        });
    },

    updateStudentStatus(studentId, newStatus) {
        if (!this.canManage() || !this.attendanceData[newStatus]) return;
        this.attendanceData.present = this.attendanceData.present.filter(id => id !== studentId);
        this.attendanceData.absent = this.attendanceData.absent.filter(id => id !== studentId);
        this.attendanceData.justified = this.attendanceData.justified.filter(id => id !== studentId);
        this.attendanceData[newStatus].push(studentId);
        this._dirty = true;
        this._paintStudent(studentId);
        this._renderCounts();
    },

    markAllStatus(status = 'present') {
        if (!this.canManage()) return;
        if (!this.enrolledStudents || !this.enrolledStudents.length) return;
        const allIds = this.enrolledStudents.map(s => s.id);
        this.attendanceData = { present: [], absent: [], justified: [] };
        this.attendanceData[status] = [...allIds];
        this._dirty = true;
        allIds.forEach(id => this._paintStudent(id));
        this._renderCounts();
    },

    closeModal() {
        this.activeSession = null;
        this._dirty = false;
    },

    async saveAttendance() {
        if (!this.canManage()) return;
        if (!this._attendanceReady) {
            if (typeof Notifications !== 'undefined') Notifications.show('Primero debe cargarse la asistencia correctamente.', 'error');
            return;
        }
        if (!this.activeSession) return;

        const btn = document.getElementById('btn-save-attendance');
        const originalText = btn ? btn.innerHTML : '';
        if (btn) {
            btn.disabled = true;
            SafeHTML.set(btn, '<i class="fas fa-spinner fa-spin"></i> Guardando…');
        }

        try {
            await AttendanceService.saveAttendance(this.activeSession.id, this.selectedDate, this.attendanceData);
            if (typeof Notifications !== 'undefined' && Notifications.show) {
                Notifications.show('Lista guardada', 'success');
            }
        } catch (error) {
            console.error('Error saving attendance:', error);
            if (typeof Notifications !== 'undefined' && Notifications.show) {
                Notifications.show(error.message || 'No se pudo guardar la asistencia. Tus cambios siguen pendientes.', 'error');
            }
            return;
        } finally {
            if (btn) {
                btn.disabled = false;
                SafeHTML.set(btn, originalText);
            }
        }
        this._dirty = false;
        this._renderCounts();
        await this.refreshStatuses();
        if (typeof CategoryDashboards !== 'undefined' && CategoryDashboards.initAcademico) CategoryDashboards.initAcademico();
    }
};

window.AttendanceView = AttendanceView;
